import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function FinalCTA() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Enter a valid work email.");
      return;
    }
    setSubmitted(true);
    toast.success("Audit request received. Our team will reach out within 24 hours.");
    setEmail("");
  };

  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="relative py-14 md:py-32 overflow-hidden"
    >
      <div className="aurora" />
      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative rounded-[2rem] glass-strong p-10 md:p-16 overflow-hidden glow-purple"
        >
          <div className="absolute inset-0 -z-10 grid-bg radial-fade opacity-30" />
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-purple-600/20 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-cyan-500/15 blur-3xl" />

          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
                / Let's build
              </div>
              <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                Ready to <span className="text-gradient-neon">scale beyond limits?</span>
              </h2>
              <p className="mt-5 text-zinc-300 max-w-md text-base leading-relaxed">
                Get a complimentary growth audit covering your funnel, paid
                channels and AI opportunity map. Delivered in 5 working days.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-400">
                {[
                  "30-min strategy diagnostic call",
                  "Personalized growth blueprint (PDF)",
                  "AI automation opportunity scan",
                ].map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-cyan-300" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div id="strategy-form">
              <form
                onSubmit={onSubmit}
                className="relative rounded-2xl glass p-2 flex flex-col sm:flex-row gap-2 items-stretch"
                data-testid="audit-form"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="founder@yourbrand.com"
                  className="flex-1 bg-transparent text-white placeholder:text-zinc-500 px-5 py-4 outline-none text-sm rounded-xl"
                  data-testid="audit-email-input"
                />
                <button
                  type="submit"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black px-6 py-3.5 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
                  data-testid="audit-submit"
                >
                  Get Free Audit
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </button>
              </form>
              {submitted && (
                <p className="mt-3 text-xs text-emerald-400 font-mono-acc tracking-wide">
                  ✓ Audit request queued — check your inbox shortly.
                </p>
              )}
              <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                No spam. No sales pressure. We respond personally within 1
                business day. Privacy-first by design.
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="#"
                  className="inline-flex items-center gap-2 rounded-full glass-strong px-5 py-2.5 text-xs text-white hover:bg-white/10 transition-colors"
                  data-testid="cta-book-call"
                >
                  Book Strategy Call
                  <span>→</span>
                </a>
                <a
                  href="#case-studies"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 px-5 py-2.5 text-xs text-zinc-300 hover:text-white hover:border-white/20 transition-colors"
                >
                  See proof
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
